import { Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { InfoTip } from '@/components/ui/InfoTip';
import { useTelemetryLineChanges } from '@/context/transaction/TelemetryLineChangeContext';

/**
 * Produces a message like "3 green cellular and 1 green satelline lines ripening"
 * (where "ripening" is the `state` and "green" is the optional `perTypeAdjective`)
 *
 * If either line type's count is zero, that clause is omitted, and if both are zero, returns undefined.
 *
 * If you don't want a trailing state, pass ''
 */
export const composeLineCountMessage = (
    counts: { iridium: number; cellular: number },
    state: string = '',
    perTypeAdjective: string = ''
) => {
    const lineDescription = compact([
        counts.cellular > 0 ? `${counts.cellular} ${perTypeAdjective} cellular` : undefined,
        counts.iridium > 0 ? `${counts.iridium} ${perTypeAdjective} satellite` : undefined,
    ]).join(' and ');

    if (!lineDescription) {
        return undefined;
    }
    return `${lineDescription} line${counts.cellular + counts.iridium === 1 ? '' : 's'} ${state}`.trim();
};

export const TransactionLineChangeSummary = ({ hideUnusedLines }: { hideUnusedLines?: boolean }) => {
    const {
        numActivatedLines,
        numDeactivatedLines,
        numExtraLines,
        hasUnusedLines,
        isRetainExtraLines,
        setRetainExtraLines,
    } = useTelemetryLineChanges();

    const extraLineMessage = useMemo(
        () => !hideUnusedLines && composeLineCountMessage(numExtraLines, 'available', 'unused'),
        [numExtraLines, hideUnusedLines]
    );

    const changesMessage = useMemo(() => {
        return compact([
            composeLineCountMessage(numActivatedLines, 'enabled'),
            composeLineCountMessage(numDeactivatedLines, 'disabled'),
        ]).join(', ');
    }, [numActivatedLines, numDeactivatedLines]);

    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {extraLineMessage && (
                <>
                    <Typography>{extraLineMessage}</Typography>
                    <Divider orientation="vertical" />
                </>
            )}
            {changesMessage && (
                <Typography>
                    <strong>Changes: </strong>
                    {changesMessage}
                </Typography>
            )}
            {hasUnusedLines && !hideUnusedLines && (
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                    <FormControlLabel
                        sx={{ mr: 0.5 }}
                        control={
                            <Checkbox
                                data-testid="retain-extra-lines-checkbox"
                                sx={{ p: 0, ml: 3, mr: 0.5 }}
                                checked={isRetainExtraLines ?? false}
                                onChange={(_, checked) => setRetainExtraLines(checked)}
                            />
                        }
                        label="Keep unused lines available in subscription"
                    />
                    <InfoTip>
                        This will keep disabled lines available for other devices to use. You will continue to be
                        charged monthly for them.
                    </InfoTip>
                </Stack>
            )}
        </Stack>
    );
};
